import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MultiSelectButton, MultiSelectSearch } from '../helpers/MultiSelect';
import {
  selectAdvancedPointFilters,
  selectPossiblePointTypes,
} from '../filters/advancedFiltersSelectors';
import { advancedFiltersActions } from '../filters/advancedFiltersSlice';
import { useEffect, useMemo } from 'react';
import { useGetTSPsQuery } from '../services/edgeApi';
import { selectTspOptions } from '../map/slice/mapSelectors';

/**
 * @typedef {{
 *  lockedTsps?: number[];
 * }} PointFiltersProps
 */

/**
 * @typedef {{
 *  focusSearchBox: () => void;
 * }} PointFiltersRef
 */

/**
 *
 * @param {PointFiltersProps} props
 * @param {React.Ref<PointFiltersRef>} ref
 */
export const _PointFilters = ({ lockedTsps = [] }, ref) => {
  useGetTSPsQuery();
  const dispatch = useDispatch();
  const advancedPointFilters = useSelector(selectAdvancedPointFilters);
  const tspOptions = useSelector(selectTspOptions);
  const pointTypes = useSelector(selectPossiblePointTypes);
  /**
   * @type {React.RefObject<HTMLInputElement | null>}
   */
  const searchBoxRef = useRef(null);

  useEffect(() => {
    if (lockedTsps?.length > 0) {
      dispatch(
        advancedFiltersActions.setFilterValue({
          type: 'points',
          field: 'tsps',
          value: Array.from(new Set(lockedTsps)),
        }),
      );
      return () => {
        dispatch(
          advancedFiltersActions.setFilterValue({
            type: 'points',
            field: 'tsps',
            value: advancedPointFilters.tsps,
          }),
        );
      };
    }
  }, [lockedTsps]);

  useImperativeHandle(
    ref,
    () => {
      return {
        focusSearchBox: () => {
          searchBoxRef.current?.focus();
        },
      };
    },
    [searchBoxRef],
  );

  const typeOptions = useMemo(
    () => pointTypes.map((type) => ({ value: type, label: type })),
    [pointTypes],
  );

  return (
    <div className="my-2 p-3">
      <div className="row">
        <div className="col">
          <MultiSelectButton
            title="Conditions"
            options={[
              { id: 'mapped=true', name: 'Mapped' },
              { id: 'mapped=false', name: 'Unmapped' },
              {
                id: 'hasCapacityComponent=true',
                name: 'Has Capacity Component',
              },
              {
                id: 'hasCapacityComponent=false',
                name: 'No Capacity Component',
              },
              // { id: 'hasFlowActivity=true', name: 'Has Flow Activity' },
              // { id: 'hasFlowActivity=false', name: 'No Flow Activity' },
              { id: 'hasNoticeComponent=true', name: 'Has Notice Component' },
              { id: 'hasNoticeComponent=false', name: 'No Notice Component' },
              // { id: 'hasNoticeActivity=true', name: 'Has Notice Activity' },
              // { id: 'hasNoticeActivity=false', name: 'No Notice Activity' },
              { id: 'hasInterconnects=true', name: 'Has Interconnects' },
              { id: 'hasInterconnects=false', name: 'No Interconnects' },
            ]}
            markedFirst={false}
            keyExtractor={(option) => option.id}
            isMarked={({ id }) => {
              const [field, value] = id.split('=');
              return advancedPointFilters[field] === JSON.parse(value);
            }}
            onToggle={(option, isMarked) => {
              const [field, value] = option.id.split('=');
              const filterValue = isMarked ? JSON.parse(value) : null;
              dispatch(
                advancedFiltersActions.setFilterValue({
                  type: 'points',
                  field,
                  value: filterValue,
                }),
              );
            }}
            optionRenderer={(option) => option.name}
          />
        </div>
        <div className="col">
          <input
            ref={searchBoxRef}
            className="form-control"
            placeholder="Search"
            value={advancedPointFilters.textSearch}
            onChange={(evt) =>
              dispatch(
                advancedFiltersActions.setFilterValue({
                  type: 'points',
                  field: 'textSearch',
                  value: evt.target.value,
                }),
              )
            }
          />
        </div>
        {lockedTsps?.length <= 0 && (
          <div className="col">
            <MultiSelectSearch
              placeholder="TSP"
              options={tspOptions}
              value={advancedPointFilters.tsps}
              onChange={(options) =>
                dispatch(
                  advancedFiltersActions.setFilterValue({
                    type: 'points',
                    field: 'tsps',
                    value: options.map((option) => option.value),
                  }),
                )
              }
            />
          </div>
        )}
        <div className="col">
          <MultiSelectSearch
            placeholder="Type"
            options={typeOptions}
            value={advancedPointFilters.loc_types}
            onChange={(options) =>
              dispatch(
                advancedFiltersActions.setFilterValue({
                  type: 'points',
                  field: 'loc_types',
                  value: options.map((option) => option.value),
                }),
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export const PointFilters = forwardRef(_PointFilters);
